/**
 * The single owner of the /curriculum URL shape. Everything that links into the
 * curriculum tree builds its href here, so the route layout under
 * app/curriculum/[lessonId] is described once.
 *
 * A lesson is addressed by its own id and nothing else: the URL carries no
 * position in any containing hierarchy, so a lesson's href never changes when
 * the curriculum around it does.
 */
export type CurriculumId = number | string;

export type LessonPathIds = {
  lessonId: CurriculumId;
};

export function lessonPath({ lessonId }: LessonPathIds): string {
  return `/curriculum/${lessonId}`;
}

/**
 * Deep links into a lesson carry either a `content` (LessonContent id) or a
 * `version` param, never both - `content` wins. Returns "" when neither is set.
 */
export function lessonContentQuery(contentParam?: string, versionParam?: string): string {
  if (contentParam) return `?content=${encodeURIComponent(contentParam)}`;
  if (versionParam) return `?version=${encodeURIComponent(versionParam)}`;
  return "";
}

/** A lesson href with its content/version deep-link params preserved. */
export function lessonHref(
  ids: LessonPathIds,
  params?: { contentParam?: string; versionParam?: string },
): string {
  return `${lessonPath(ids)}${lessonContentQuery(params?.contentParam, params?.versionParam)}`;
}

/** The flashcards sub-page of a lesson, scoped to a specific generated content. */
export function flashcardsPath(ids: LessonPathIds, lessonContentId: CurriculumId): string {
  return `${lessonPath(ids)}/flashcards?content=${encodeURIComponent(String(lessonContentId))}`;
}
